// Microsoft Clarity — free session replay + heatmaps. Qualitative complement to the
// claim-funnel events: the events say WHERE a supplier stopped, Clarity lets us WATCH
// the actual session to understand WHY. Loads only when REACT_APP_CLARITY_ID is set
// (Vercel env var), so it stays inert in dev / until a project is created. Clarity
// masks text content by default. Injected once, best-effort, never blocks render.
//
// NEVER ON TEAM PAGES (owner, 2026-11-01): the admin consoles and the agent page
// show trader and supplier data, so Clarity must never record them. Two rules
// make that hold in a single-page app:
//   1. initClarity() does not load the script on a blocked path.
//   2. A document where the script HAS loaded never renders a blocked page:
//      ClarityGuard (App.js) renders nothing and reloads instead, so the
//      console opens in a fresh document with no Clarity in it.
// Team accounts (admin / super_admin) on public pages carry the custom tag
// internal=true, so their sessions can be filtered out in Clarity.

const BLOCKED_PREFIXES = ['/admin', '/agent'];   // /admin covers /admin, /admin2/*, /admin-seed

let internal = false;

export function clarityBlocked(path) {
  const p = String(path || '');
  return BLOCKED_PREFIXES.some((b) => p.startsWith(b));
}

// The script (or its queue stub) is in this document.
export function clarityLoaded() {
  return typeof window !== 'undefined' && typeof window.clarity === 'function';
}

function applyInternal() {
  try { if (internal && clarityLoaded()) window.clarity('set', 'internal', 'true'); } catch { /* never break the page */ }
}

export function initClarity(path = typeof window !== 'undefined' ? window.location.pathname : '') {
  try {
    const id = process.env.REACT_APP_CLARITY_ID;
    if (!id || typeof window === 'undefined' || window.clarity || clarityBlocked(path)) return;
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', id);
    applyInternal();
    // Clarity restarts on every in-app page change; a custom tag set before the
    // restart is not on the new session. Re-apply «internal» each time.
    window.clarity('metadata', applyInternal, false, true);
  } catch { /* analytics must never break the app */ }
}

// Admin and super_admin accounts are the team. Remembered, so a script loaded
// later in the visit (console → public page) is tagged too.
export function setClarityInternal(isTeam) {
  internal = !!isTeam;
  applyInternal();
}

// A team page in a document that already has Clarity: must not render.
export function mustReloadWithoutClarity(path) {
  return clarityBlocked(path) && clarityLoaded();
}

export function __resetClarityForTests() { internal = false; }
